/**
 * Created by neo on 18.01.17.
 */

import { makeAutoObservable, toJS } from 'mobx';
import { v4 as UUID } from 'uuid';
import { LocalizedValue, createLocalizedValue } from '../../../LocalizedValue';
import { ExerciseVariation, createExerciseVariation } from '../../../ProgramPortfolio/ExerciseVariation';
import { RoundInfo, createRoundInfo } from '../../../ProgramPortfolio/RoundInfo';
import { CoachWorkoutPhase } from './CoachWorkoutPhase';
import { Media } from '../../../Media/Media';
import { languagePriority } from '../../../LocalizedEntity';
import { CoachExerciseBlockSet, createCoachExerciseBlockSet } from './CoachExerciseBlockSet';
import { TrackingKeysList, BaseTrackingKey } from '../../../ProgramPortfolio/TrackingKeys';
import { HttpBackend } from '../../../../Services/Http/HttpBackend';
import { PipelineContext, createPipelineContext } from '../../PipelineContext';
import { logger } from '../../../../Utils/logger';
import { ExerciseBlock } from '../../../ProgramPortfolio/ExerciseBlock';
import { languageState } from '../../../../Contexts/LanguageContext';

export type CoachExerciseBlock = {
  exerciseBlockId: string;
  description: LocalizedValue[];
  exercise: ExerciseVariation;
  sets: CoachExerciseBlockSet[];
  roundInfo?: RoundInfo;
  phase: CoachWorkoutPhase;
  readonly valid: boolean;
  readonly duration: number;
  readonly breakTime: number;
  readonly totalTimeExercising: number;
  readonly tons: number;
  readonly lastSet: CoachExerciseBlockSet | undefined;
  readonly firstSet: CoachExerciseBlockSet | undefined;
  readonly prevSet: CoachExerciseBlockSet | undefined;
  readonly followingSet: CoachExerciseBlockSet | undefined;
  readonly exerciseBlockIndex: number;
  readonly isLastBlock: boolean;
  readonly prevBlock: CoachExerciseBlock | undefined;
  readonly nextBlock: CoachExerciseBlock | undefined;
  readonly defaultTrackingKeys: BaseTrackingKey[];
  readonly trackingParameters: BaseTrackingKey[];
  readonly sortedTrackingParameters: BaseTrackingKey[];
  readonly spotClassMedia: Media | undefined;
  readonly estimatedTotalExerciseTime: number;
  readonly translatedDescription: string;
  readonly videoMedia: Media | undefined;
  readonly hasReplaceSuggestions: boolean | undefined;
  setBasicData(json: Partial<CoachExerciseBlock> | CoachExerciseBlock): void;
  setData(json: Partial<CoachExerciseBlock> | CoachExerciseBlock): void;
  calculateCalories(bmr24: number): number;
  suggestNew(params?: PipelineContext): Promise<CoachExerciseBlock[]>;
  replace(newBlock: CoachExerciseBlock): Promise<CoachExerciseBlock>;
  replaceRemote(newBlock: CoachExerciseBlock): Promise<CoachExerciseBlock>;
  createSet(setValues?: any): CoachExerciseBlockSet;
  cleanValues(values: any): any;
  removeSet(index: number): void;
  save(): Promise<boolean>;
};

const isCoachExerciseBlockInstance = (obj: any): obj is CoachExerciseBlock =>
  obj && typeof obj.toJS === 'function' && typeof obj.setData === 'function';

export function createCoachExerciseBlock(
  phase: CoachWorkoutPhase,
  json?: Partial<CoachExerciseBlock>,
): CoachExerciseBlock {
  const block: CoachExerciseBlock = makeAutoObservable({
    exerciseBlockId: json?.exerciseBlockId || UUID(),
    description: (json?.description ?? []).map((l) => createLocalizedValue(l)),
    exercise: createExerciseVariation(json?.exercise),
    sets: [] as CoachExerciseBlockSet[],
    roundInfo: json?.roundInfo ? createRoundInfo(json.roundInfo) : undefined,
    phase,

    get valid(): boolean {
      return true;
    },

    get duration(): number {
      return this.sets.reduce((total, set) => total + set.totalDuration, 0);
    },

    get breakTime(): number {
      return this.sets.reduce((total, set) => total + set.breakTime, 0);
    },

    get totalTimeExercising(): number {
      return this.sets.reduce((total, set) => total + set.plannedDurationMs, 0);
    },

    get tons(): number {
      return this.sets.reduce((total, set) => total + set.tons, 0);
    },

    get lastSet(): CoachExerciseBlockSet | undefined {
      return this.sets.length > 0 ? this.sets[this.sets.length - 1] : undefined;
    },

    get firstSet(): CoachExerciseBlockSet | undefined {
      return this.sets.length > 0 ? this.sets[0] : undefined;
    },

    get prevSet(): CoachExerciseBlockSet | undefined {
      return this.lastSet || (this.prevBlock ? this.prevBlock.prevSet : undefined);
    },

    get followingSet(): CoachExerciseBlockSet | undefined {
      return this.firstSet || (this.nextBlock ? this.nextBlock.followingSet : undefined);
    },

    get exerciseBlockIndex(): number {
      return this.phase ? this.phase.exerciseBlocks.findIndex((b) => b.exerciseBlockId === this.exerciseBlockId) : -1;
    },

    get isLastBlock(): boolean {
      return this.phase ? this.exerciseBlockIndex + 1 === this.phase.exerciseBlocks.length : true;
    },

    get prevBlock(): CoachExerciseBlock | undefined {
      if (this.phase) {
        const { exerciseBlockIndex } = this;
        if (exerciseBlockIndex === 0) {
          return this.phase.prevPhase?.prevBlock;
        }
        return this.phase.exerciseBlocks[exerciseBlockIndex - 1];
      }
      return undefined;
    },

    get nextBlock(): CoachExerciseBlock | undefined {
      if (this.phase) {
        const { exerciseBlockIndex, isLastBlock } = this;
        if (isLastBlock) {
          return this.phase.nextPhase?.nextBlock;
        }
        const nextIndex = exerciseBlockIndex + 1;
        return nextIndex < this.phase.exerciseBlocks.length ? this.phase.exerciseBlocks[nextIndex] : undefined;
      }
      return undefined;
    },

    get defaultTrackingKeys(): BaseTrackingKey[] {
      return this.exercise.trackingParameters;
    },

    get trackingParameters(): BaseTrackingKey[] {
      return this.defaultTrackingKeys;
    },

    get sortedTrackingParameters(): BaseTrackingKey[] {
      return this.trackingParameters.slice().sort((a, b) => TrackingKeysList.indexOf(a) - TrackingKeysList.indexOf(b));
    },

    get spotClassMedia(): Media | undefined {
      return this.exercise.imageMedia;
    },

    get estimatedTotalExerciseTime(): number {
      return this.sets.reduce((total, set) => total + set.totalDuration, 0);
    },

    get translatedDescription(): string {
      const language = languageState.language;
      const langFit = this.description.find((n) => n.lang === language.toLowerCase())?.value;
      if (langFit?.trim()) {
        return langFit;
      }
      for (const lang of languagePriority) {
        const entry = this.description.find((l) => l.lang === lang);
        if (entry) {
          return entry.value ?? '';
        }
      }
      return this.description[0]?.value ?? '';
    },

    get videoMedia(): Media | undefined {
      return this.exercise.videoMedia;
    },

    get hasReplaceSuggestions(): boolean | undefined {
      return this.phase.hasReplaceSuggestions;
    },

    setBasicData(json: Partial<CoachExerciseBlock> | CoachExerciseBlock): void {
      this.exerciseBlockId = json.exerciseBlockId || UUID();
      this.description = (json.description ?? []).map((l) => createLocalizedValue(l));
      this.exercise = isCoachExerciseBlockInstance(json) ? json.exercise : createExerciseVariation(json.exercise);
    },

    setData(json: Partial<CoachExerciseBlock> | CoachExerciseBlock): void {
      this.setBasicData(json);
      this.sets = isCoachExerciseBlockInstance(json)
        ? json.sets
        : (json.sets || []).map((s) => createCoachExerciseBlockSet(this, s));
    },

    calculateCalories(bmr24: number): number {
      return this.sets.reduce((total, set) => total + set.calculateCalories(bmr24), 0);
    },

    suggestNew(params?: PipelineContext): Promise<CoachExerciseBlock[]> {
      const context = params ?? createPipelineContext();
      context.excludeExerciseIds = this.phase.exerciseBlocks.map((b) => b.exercise.id);
      return HttpBackend.post(`/coach/workout/${this.phase.workout.id}/${this.phase.phaseId}/suggest`, context).then(
        (result) => result.map((r: CoachExerciseBlock) => createCoachExerciseBlock(this.phase, r)),
      );
    },

    replace(newBlock: CoachExerciseBlock): Promise<CoachExerciseBlock> {
      const promise = this.replaceRemote(newBlock);
      this.setData(newBlock);
      return promise;
    },

    replaceRemote(newBlock: CoachExerciseBlock): Promise<CoachExerciseBlock> {
      return HttpBackend.post(`/coach/workout/${this.phase.workout.id}/replaceExerciseBlock`, {
        exerciseBlockId: this.exerciseBlockId,
        exerciseBlock: newBlock,
      }).then(() => this);
    },

    createSet(setValues?: any): CoachExerciseBlockSet {
      const { lastSet } = this;
      const lastOrDefault = lastSet ? toJS(lastSet.values) : {};
      const values = setValues || lastOrDefault;
      logger('valuesPassed::', values);
      const set = createCoachExerciseBlockSet(this, { values: this.cleanValues(values) });
      logger('set::', set);
      this.sets.push(set);
      return set;
    },

    cleanValues(values: any): any {
      return TrackingKeysList.filter(
        (k: string) => undefined !== values[k] || undefined !== values[`MIN_${k}`] || undefined !== values[`MAX_${k}`],
      )
        .map((k: string) => [
          k,
          values[`MIN_${k}`] || values[`MAX_${k}`] || values[k],
          undefined !== values[k]
            ? values[k]
            : values[`MAX_${k}`] === 0
              ? values[`MAX_${k}`]
              : values[`MAX_${k}`] || values[`MIN_${k}`],
        ])
        .reduce((obj: any, curr: any[]) => {
          obj[`MIN_${curr[0]}`] = curr[2] === 0 ? curr[1] : Math.min(curr[1], curr[2]);
          obj[`MAX_${curr[0]}`] = curr[2] === 0 ? curr[2] : Math.max(curr[1], curr[2]);
          return obj;
        }, {});
    },

    removeSet(index: number): void {
      this.sets.splice(index, 1);
    },

    async save(): Promise<boolean> {
      return true;
    },
  });

  block.sets = (json?.sets || []).map((s) => createCoachExerciseBlockSet(block, s));

  return block;
}
